import React, { useState, useMemo } from 'react';
import axios from 'axios';
import { Send, Loader2, Mail, X } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Shared reminder dialog. items = [{ type: 'task'|'activity', id, title, owner_name, owner_email? }]
 * Backend groups by owner and sends one email per grupo.
 */
const RemindDialog = ({ open, onOpenChange, items = [], onSent }) => {
  const [ccInput, setCcInput] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);

  // Group items by owner for the preview
  const grouped = useMemo(() => {
    const g = {};
    for (const it of items) {
      const key = (it.owner_email || it.owner_name || 'sin_responsable').toLowerCase();
      if (!g[key]) g[key] = { owner_name: it.owner_name || 'Sin responsable', owner_email: it.owner_email || '', tasks: [], activities: [] };
      if (it.type === 'task') g[key].tasks.push(it);
      else g[key].activities.push(it);
    }
    return Object.values(g);
  }, [items]);

  const totalTasks = items.filter(i => i.type === 'task').length;
  const totalActs = items.filter(i => i.type === 'activity').length;

  const parseCc = () => {
    return ccInput
      .split(/[,;\s]+/)
      .map(s => s.trim())
      .filter(s => s && /.+@.+\..+/.test(s));
  };

  const handleSend = async () => {
    if (items.length === 0) return;
    setLoading(true);
    try {
      const payload = {
        items: items.map(i => ({ type: i.type, id: i.id })),
        cc_emails: parseCc(),
        custom_message: message.trim(),
      };
      const { data } = await axios.post(`${API}/reminders/send`, payload);
      if (data.sent > 0) {
        toast.success(`Enviado${data.sent > 1 ? 's' : ''} ${data.sent} recordatorio${data.sent > 1 ? 's' : ''}`);
      }
      if (data.failed > 0) {
        toast.error(`${data.failed} recordatorio(s) fallaron`);
      }
      onSent && onSent(data);
      setMessage('');
      setCcInput('');
      onOpenChange(false);
    } catch (err) {
      console.error('Reminder send error', err);
      const msg = err.response?.data?.detail || 'No se pudo enviar el recordatorio';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="remind-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mail className="w-5 h-5 text-primary" />
            Recordar por correo
          </DialogTitle>
          <DialogDescription>
            Se envía un correo por cada responsable con sus pendientes. Los destinatarios y CC quedan en el cuerpo del correo para que Power Automate haga el ruteo.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Summary */}
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <Badge variant="secondary">{items.length} elemento{items.length !== 1 ? 's' : ''}</Badge>
            {totalTasks > 0 && <Badge className="bg-red-100 text-red-700">{totalTasks} tarea{totalTasks !== 1 ? 's' : ''}</Badge>}
            {totalActs > 0 && <Badge className="bg-amber-100 text-amber-700">{totalActs} actividad{totalActs !== 1 ? 'es' : ''}</Badge>}
            <span className="text-muted-foreground">→ {grouped.length} correo{grouped.length !== 1 ? 's' : ''} (uno por responsable)</span>
          </div>

          {/* Group preview */}
          <div className="rounded-md border">
            <ScrollArea className="max-h-56">
              <div className="divide-y">
                {grouped.map((g, idx) => (
                  <div key={idx} className="p-3" data-testid={`remind-group-${idx}`}>
                    <div className="flex items-center justify-between gap-2 mb-1">
                      <div className="font-medium text-sm truncate">{g.owner_name}</div>
                      <div className="text-xs text-muted-foreground font-mono truncate">{g.owner_email || '(email desconocido)'}</div>
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {g.tasks.length > 0 && <span>{g.tasks.length} tarea{g.tasks.length !== 1 ? 's' : ''}</span>}
                      {g.tasks.length > 0 && g.activities.length > 0 && <span> · </span>}
                      {g.activities.length > 0 && <span>{g.activities.length} actividad{g.activities.length !== 1 ? 'es' : ''}</span>}
                    </div>
                  </div>
                ))}
              </div>
            </ScrollArea>
          </div>

          {/* CC input */}
          <div className="space-y-1.5">
            <Label htmlFor="remind-cc">CC (opcional)</Label>
            <Input
              id="remind-cc"
              placeholder="email1@typsa.es, email2@typsa.es"
              value={ccInput}
              onChange={(e) => setCcInput(e.target.value)}
              data-testid="remind-cc-input"
            />
            <p className="text-xs text-muted-foreground">Separados por coma, espacio o punto y coma. Se añaden al bloque &quot;CC&quot; del cuerpo.</p>
          </div>

          {/* Custom message */}
          <div className="space-y-1.5">
            <Label htmlFor="remind-msg">Mensaje personal (opcional)</Label>
            <Textarea
              id="remind-msg"
              placeholder="Escribe un mensaje breve que quieras añadir al recordatorio&hellip;"
              rows={3}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              data-testid="remind-message"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={loading} data-testid="remind-cancel">
            <X className="w-4 h-4 mr-1" />
            Cancelar
          </Button>
          <Button onClick={handleSend} disabled={loading || items.length === 0} data-testid="remind-send">
            {loading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Send className="w-4 h-4 mr-1" />}
            Enviar recordatorio{grouped.length > 1 ? 's' : ''}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default RemindDialog;
